/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
    combineCodec,
    getAddressDecoder,
    getAddressEncoder,
    getStructDecoder,
    getStructEncoder,
    getU8Decoder,
    getU8Encoder,
    transformEncoder,
    type AccountMeta,
    type Address,
    type FixedSizeCodec,
    type FixedSizeDecoder,
    type FixedSizeEncoder,
    type Instruction,
    type InstructionWithAccounts,
    type InstructionWithData,
    type ReadonlyUint8Array,
    type WritableAccount,
} from '@solana/kit';
import { TOKEN_2022_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const INITIALIZE_PERMISSIONED_BURN_DISCRIMINATOR = 46;

export function getInitializePermissionedBurnDiscriminatorBytes() {
    return getU8Encoder().encode(INITIALIZE_PERMISSIONED_BURN_DISCRIMINATOR);
}

export const INITIALIZE_PERMISSIONED_BURN_PERMISSIONED_BURN_DISCRIMINATOR = 0;

export function getInitializePermissionedBurnPermissionedBurnDiscriminatorBytes() {
    return getU8Encoder().encode(INITIALIZE_PERMISSIONED_BURN_PERMISSIONED_BURN_DISCRIMINATOR);
}

export type InitializePermissionedBurnInstruction<
    TProgram extends string = typeof TOKEN_2022_PROGRAM_ADDRESS,
    TAccountMint extends string | AccountMeta<string> = string,
    TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
    InstructionWithData<ReadonlyUint8Array> &
    InstructionWithAccounts<
        [TAccountMint extends string ? WritableAccount<TAccountMint> : TAccountMint, ...TRemainingAccounts]
    >;

export type InitializePermissionedBurnInstructionData = {
    discriminator: number;
    permissionedBurnDiscriminator: number;
    /** The public key for the account that is required for token burning. */
    authority: Address;
};

export type InitializePermissionedBurnInstructionDataArgs = {
    /** The public key for the account that is required for token burning. */
    authority: Address;
};

export function getInitializePermissionedBurnInstructionDataEncoder(): FixedSizeEncoder<InitializePermissionedBurnInstructionDataArgs> {
    return transformEncoder(
        getStructEncoder([
            ['discriminator', getU8Encoder()],
            ['permissionedBurnDiscriminator', getU8Encoder()],
            ['authority', getAddressEncoder()],
        ]),
        value => ({
            ...value,
            discriminator: INITIALIZE_PERMISSIONED_BURN_DISCRIMINATOR,
            permissionedBurnDiscriminator: INITIALIZE_PERMISSIONED_BURN_PERMISSIONED_BURN_DISCRIMINATOR,
        }),
    );
}

export function getInitializePermissionedBurnInstructionDataDecoder(): FixedSizeDecoder<InitializePermissionedBurnInstructionData> {
    return getStructDecoder([
        ['discriminator', getU8Decoder()],
        ['permissionedBurnDiscriminator', getU8Decoder()],
        ['authority', getAddressDecoder()],
    ]);
}

export function getInitializePermissionedBurnInstructionDataCodec(): FixedSizeCodec<
    InitializePermissionedBurnInstructionDataArgs,
    InitializePermissionedBurnInstructionData
> {
    return combineCodec(
        getInitializePermissionedBurnInstructionDataEncoder(),
        getInitializePermissionedBurnInstructionDataDecoder(),
    );
}

export type InitializePermissionedBurnInput<TAccountMint extends string = string> = {
    /** The mint account to initialize. */
    mint: Address<TAccountMint>;
    authority: InitializePermissionedBurnInstructionDataArgs['authority'];
};

export function getInitializePermissionedBurnInstruction<
    TAccountMint extends string,
    TProgramAddress extends Address = typeof TOKEN_2022_PROGRAM_ADDRESS,
>(
    input: InitializePermissionedBurnInput<TAccountMint>,
    config?: { programAddress?: TProgramAddress },
): InitializePermissionedBurnInstruction<TProgramAddress, TAccountMint> {
    // Program address.
    const programAddress = config?.programAddress ?? TOKEN_2022_PROGRAM_ADDRESS;

    // Original accounts.
    const originalAccounts = { mint: { value: input.mint ?? null, isWritable: true } };
    const accounts = originalAccounts as Record<keyof typeof originalAccounts, ResolvedAccount>;

    // Original args.
    const args = { ...input };

    const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
    return Object.freeze({
        accounts: [getAccountMeta(accounts.mint)],
        data: getInitializePermissionedBurnInstructionDataEncoder().encode(
            args as InitializePermissionedBurnInstructionDataArgs,
        ),
        programAddress,
    } as InitializePermissionedBurnInstruction<TProgramAddress, TAccountMint>);
}

export type ParsedInitializePermissionedBurnInstruction<
    TProgram extends string = typeof TOKEN_2022_PROGRAM_ADDRESS,
    TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
    programAddress: Address<TProgram>;
    accounts: {
        /** The mint account to initialize. */
        mint: TAccountMetas[0];
    };
    data: InitializePermissionedBurnInstructionData;
};

export function parseInitializePermissionedBurnInstruction<
    TProgram extends string,
    TAccountMetas extends readonly AccountMeta[],
>(
    instruction: Instruction<TProgram> &
        InstructionWithAccounts<TAccountMetas> &
        InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializePermissionedBurnInstruction<TProgram, TAccountMetas> {
    if (instruction.accounts.length < 1) {
        // TODO: Coded error.
        throw new Error('Not enough accounts');
    }
    let accountIndex = 0;
    const getNextAccount = () => {
        const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
        accountIndex += 1;
        return accountMeta;
    };
    return {
        programAddress: instruction.programAddress,
        accounts: { mint: getNextAccount() },
        data: getInitializePermissionedBurnInstructionDataDecoder().decode(instruction.data),
    };
}
